import { Controller } from 'stimulus'

// Page 3 (Group Membership). Two mutually-exclusive checkboxes: "covers other
// companies" (reveals the subsidiary builder) and "not covering any other
// companies". Exactly one must be ticked to proceed — enforced in validate()
// on Save. Add/Remove clone or drop subsidiary cards from a <template>. Each card
// carries its own `subsidiary-contact` controller for the contact reveal.
export default class extends Controller {
  static targets = [
    'builder',
    'list',
    'template',
    'coversCheckbox',
    'noneCheckbox',
    'error',
  ]

  connect() {
    this.index = this.subsidiaryCount
    this.restoreDraft()
    this.renumber()
  }

  // Runs before submit-confirm#open on Save. Require one checkbox, and — when
  // "covers" is chosen — at least one company. stopImmediatePropagation prevents
  // the confirmation modal opening on the same click.
  validate(event) {
    if (
      !this.coversCheckboxTarget.checked &&
      !this.noneCheckboxTarget.checked
    ) {
      this.block(
        event,
        'Please tell us whether your membership covers other companies.'
      )
    } else if (
      this.coversCheckboxTarget.checked &&
      this.subsidiaryCount === 0
    ) {
      this.block(
        event,
        'Please add at least one company, or select "My membership is not covering any other companies".'
      )
    } else {
      this.hideError()
    }
  }

  // "My membership covers other companies" — mutually exclusive with the other box.
  toggleCovers(event) {
    if (event.target.checked) {
      this.noneCheckboxTarget.checked = false
      this.builderTarget.style.display = ''
      if (this.subsidiaryCount === 0) this.add()
    } else {
      this.builderTarget.style.display = 'none'
      this.listTarget.innerHTML = ''
    }
    this.hideError()
  }

  // "My membership is not covering any other companies".
  toggleNone(event) {
    if (event.target.checked) {
      this.coversCheckboxTarget.checked = false
      this.builderTarget.style.display = 'none'
      this.listTarget.innerHTML = ''
    }
    this.hideError()
  }

  add() {
    const markup = this.templateTarget.innerHTML.replace(
      /__INDEX__/g,
      this.index
    )
    this.listTarget.insertAdjacentHTML('beforeend', markup)
    this.index += 1
    this.renumber()
    this.hideError()
  }

  remove(event) {
    event.preventDefault()
    event.currentTarget.closest('.subsidiary').remove()
    this.renumber()
  }

  // Keep the "Company Details #N" headers in sequence as cards are added/removed.
  renumber() {
    this.listTarget.querySelectorAll('.subsidiary').forEach((card, i) => {
      const label = card.querySelector('.subsidiary-number')
      if (label) label.textContent = `#${i + 1}`
    })
  }

  get subsidiaryCount() {
    return this.listTarget.querySelectorAll('.subsidiary').length
  }

  block(event, message) {
    event.preventDefault()
    event.stopImmediatePropagation()
    this.showError(message)
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.style.display = ''
  }

  hideError() {
    if (this.hasErrorTarget) this.errorTarget.style.display = 'none'
  }

  // --- Draft (sessionStorage) so an in-progress Page 3 survives the Back
  //     round-trip to Page 2 without a server save. Same-tab/session only. ---

  get draftKey() {
    return `membership-page3:${this.data.get('key')}`
  }

  // Wired to the Back link; runs synchronously before the link navigates.
  // Serialises each card by field name (last [...] segment), not by index, so
  // gaps from removed cards normalise on rebuild.
  saveDraft() {
    const companies = Array.from(
      this.listTarget.querySelectorAll('.subsidiary')
    ).map((card) => {
      const data = {}
      card.querySelectorAll('input').forEach((el) => {
        const match = el.name.match(/\[(\w+)\]$/)
        if (!match) return
        if (el.type === 'checkbox') data[match[1]] = el.checked
        else if (el.type !== 'hidden') data[match[1]] = el.value
      })
      return data
    })

    sessionStorage.setItem(
      this.draftKey,
      JSON.stringify({
        covers: this.coversCheckboxTarget.checked,
        none: this.noneCheckboxTarget.checked,
        companies,
      })
    )
  }

  // Only rebuild when the list is empty, so we never fight server-rendered cards
  // (a resume or a failed-submit re-render) or re-restore after a real save.
  restoreDraft() {
    if (this.subsidiaryCount > 0) return

    const raw = sessionStorage.getItem(this.draftKey)
    if (!raw) return
    sessionStorage.removeItem(this.draftKey)

    let draft
    try {
      draft = JSON.parse(raw)
    } catch (_e) {
      return
    }

    if (draft.none) {
      this.noneCheckboxTarget.checked = true
      return
    }
    if (!draft.covers) return

    this.coversCheckboxTarget.checked = true
    this.builderTarget.style.display = ''

    const companies = draft.companies || []
    if (companies.length === 0) {
      this.add()
      return
    }
    companies.forEach((data) => this.addFilledCard(data))
  }

  addFilledCard(data) {
    this.add()
    const card = this.listTarget.lastElementChild
    Object.entries(data).forEach(([field, value]) => {
      if (typeof value === 'boolean') {
        // Setting .checked before Stimulus connects the new card's
        // subsidiary-contact controller lets its connect()->toggle() reveal the
        // contact block from the restored state.
        const box = card.querySelector(
          `input[type=checkbox][name$="[${field}]"]`
        )
        if (box) box.checked = value
      } else {
        const input = card.querySelector(
          `input[name$="[${field}]"]:not([type=hidden])`
        )
        if (input) input.value = value
      }
    })
  }
}
